// 📄 components/organizacion/CreateTeamOrgModal.tsx
//
// Reemplaza a OrganizationFormModal.tsx (vista SSO legado, eliminada en
// Sub-fase F). Formulario mínimo: solo el nombre del equipo — el resto
// (type:'equipo', ownerId) lo completa createTeamOrganization() con el
// usuario actual. Movido de pages/Admin/components/ a acá (sin ningún
// cambio de lógica): no tiene ninguna dependencia admin-only — cualquier
// usuario autenticado ya podía crear su propio equipo por firestore.rules
// (ver esOrgPersonalPropia/create de organizations/{orgId}), pero el único
// botón que lo disparaba vivía atrás de /admin (AdminRoute). Ahora se
// dispara también desde OrgSwitcher.tsx, disponible para cualquier
// usuario — el resto del panel de administración (Gestión de Usuarios,
// catálogos, etc.) sigue siendo exclusivo del Admin de plataforma.
import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { createTeamOrganization } from '../../services/organizationService.ts';
import LimiteUsoAlerta from '../ui/LimiteUsoAlerta.tsx';

interface CreateTeamOrgModalProps {
  onClose: () => void;
  onCreated: () => void;
}

export default function CreateTeamOrgModal({ onClose, onCreated }: CreateTeamOrgModalProps) {
  const { user, profile } = useAuth();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('El nombre del equipo es obligatorio.');
      return;
    }
    if (!user) {
      setError('Sesión no disponible, recargá la página.');
      return;
    }

    setSaving(true);
    try {
      await createTeamOrganization(user.uid, user.email || '', name);
      onCreated();
    } catch (err: any) {
      setError(err.message || 'Error al crear el equipo.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">Crear equipo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <LimiteUsoAlerta
            cantidad={profile?.cantidadEquiposCreados ?? 0}
            limite={profile?.limiteEquipos}
            etiqueta="equipos"
          />

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Nombre del equipo <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ej. Equipo Ariba"
              autoFocus
              className="w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500"
            />
            <p className="text-[10px] text-gray-500 mt-1">Vas a quedar como owner de este equipo.</p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Creando...' : 'Crear equipo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
